import { Alpine as AlpineType } from 'alpinejs';

type SwiperLike = {
  update: () => void;
  slideTo: (i: number, speed?: number) => void;
};

function getWindowSwiper(): (new (el: HTMLElement, opts: object) => SwiperLike) | undefined {
  if (typeof window === 'undefined') {
    return undefined;
  }
  return (window as unknown as { Swiper?: new (el: HTMLElement, opts: object) => SwiperLike })
    .Swiper;
}

export default (Alpine: AlpineType) => {
  Alpine.data('productCategoryCarouselIndex', () => ({
    activeBlock: 0,
    _swipers: [] as (SwiperLike | undefined)[],

    getSwiperEl(index: number): HTMLElement | null {
      const r = (this as unknown as { $refs: Record<string, unknown> }).$refs?.[
        `pccIndexMain${index}`
      ] as HTMLElement | undefined;
      return r ?? null;
    },

    ensureMainSwiper(index: number) {
      const Swiper = getWindowSwiper();
      if (!Swiper) {
        return;
      }
      const el = this.getSwiperEl(index);
      if (!el) {
        return;
      }
      if (el.classList.contains('swiper-initialized')) {
        if (!this._swipers[index] && (el as unknown as { swiper?: SwiperLike }).swiper) {
          this._swipers[index] = (el as unknown as { swiper: SwiperLike }).swiper;
        }
        return;
      }
      const nextEl = el.querySelector<HTMLElement>('.swiper-button-next-main');
      const prevEl = el.querySelector<HTMLElement>('.swiper-button-prev-main');
      const s = new Swiper(el, {
        loop: false,
        grabCursor: true,
        spaceBetween: 20,
        slidesPerView: 'auto' as const,
        slidesPerGroup: 1,
        mousewheel: false,
        allowTouchMove: true,
        observer: true,
        observeParents: true,
        navigation:
          nextEl && prevEl
            ? {
                nextEl,
                prevEl,
              }
            : undefined,
      } as object);
      this._swipers[index] = s;
    },

    waitForSwiperAndRun(callback: () => void, attempts = 0) {
      if (getWindowSwiper()) {
        callback();
        return;
      }
      if (attempts > 400) {
        return;
      }
      requestAnimationFrame(() => this.waitForSwiperAndRun(callback, attempts + 1));
    },

    init() {
      (this as unknown as { $nextTick: (fn: () => void) => void }).$nextTick(() => {
        this.waitForSwiperAndRun(() => {
          this.ensureMainSwiper(this.activeBlock);
          (this as unknown as { $nextTick: (fn: () => void) => void }).$nextTick(() => {
            this._swipers[this.activeBlock]?.update();
          });
        });
      });
    },

    async setActiveBlock(index: number) {
      (this as unknown as { activeBlock: number }).activeBlock = index;
      await (this as unknown as { $nextTick: () => Promise<void> }).$nextTick();
      this.waitForSwiperAndRun(() => {
        this.ensureMainSwiper(index);
        const s = this._swipers[index];
        s?.update();
        s?.slideTo(0, 0);
      });
    },
  }));
};
